import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { usePlatform } from '../context/PlatformContext';
import Modal from '../components/ui/Modal';
import Badge from '../components/ui/Badge';
import QRCodeDisplay from '../components/ui/QRCodeDisplay';
import { getVerificationURL } from '../utils/qrGenerator';
import { downloadCertificatePDF } from '../utils/pdfGenerator';
import confetti from 'canvas-confetti';
import {
  FiAward, FiDownload, FiShare2, FiLink, FiCopy, FiExternalLink, FiStar,
  FiClock, FiShield, FiUser, FiEye, FiEyeOff, FiPlus
} from 'react-icons/fi';

const BADGE_PRESETS = [
  { type: 'skill', label: 'React Expert', sublabel: 'Frontend' },
  { type: 'achievement', label: 'Hackathon Winner', sublabel: 'Q2 2026' },
  { type: 'completion', label: 'Backend Cert', sublabel: 'Node.js' },
  { type: 'excellence', label: 'Best Intern', sublabel: 'Cohort 4' },
  { type: 'skill', label: 'Cloud Badge', sublabel: 'AWS' },
  { type: 'achievement', label: 'Speaker', sublabel: 'Tech Talk' },
];

export default function Wallet() {
  const { allData, allSettings, settings, wallet, updatePrivacySetting, addBadge } = usePlatform();
  const [shareModal, setShareModal] = useState(null);
  const [addBadgeModal, setAddBadgeModal] = useState(false);
  const [newBadge, setNewBadge] = useState({ type: 'skill', label: '', sublabel: '' });

  const allCandidates = [
    ...(allData.internship?.candidates || []).map(c => ({ ...c, mode: 'internship' })),
    ...(allData.certificate?.candidates || []).map(c => ({ ...c, mode: 'certificate' })),
  ].filter(c => c.docStatus === 'generated');

  const handleShare = (cred) => {
    confetti({ particleCount: 80, spread: 60, origin: { y: 0.7 }, colors: ['#0EA5E9', '#3B82F6', '#8B5CF6'] });
    setShareModal(cred);
  };

  const handleAddBadge = () => {
    if (!newBadge.label.trim()) return toast.error('Badge label is required.');
    addBadge({ ...newBadge, id: crypto.randomUUID(), earnedAt: new Date().toISOString() });
    toast.success('Badge added to wallet!');
    setNewBadge({ type: 'skill', label: '', sublabel: '' });
    setAddBadgeModal(false);
  };

  const employerShareLink = (cred) => `${window.location.origin}/#/verify/${cred.verificationId || cred.generatedId}?source=employer`;

  return (
    <div className="space-y-8 pb-16">
      {/* Header */}
      <div className="flex justify-between items-end">
        <div>
          <h1 className="page-title">Digital Credential Wallet</h1>
          <p className="page-subtitle">All your credentials, badges, and shareable achievements in one place.</p>
        </div>
        <div className="flex gap-3">
          <span className="px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-600">{allCandidates.length} Credentials</span>
          <span className="px-4 py-2.5 bg-violet-50 border border-violet-100 rounded-xl text-sm font-bold text-violet-600">{wallet.badges.length} Badges</span>
        </div>
      </div>

      {/* Credentials Grid */}
      <section>
        <h2 className="text-xl font-semibold text-navy-900 mb-4 flex items-center gap-3">
          <span className="w-8 h-8 rounded-xl bg-sky-50 text-sky-600 flex items-center justify-center"><FiShield className="w-4 h-4" /></span>
          Credentials
        </h2>
        {allCandidates.length === 0 ? (
          <div className="glass-card rounded-2xl p-10 text-center">
            <FiAward className="w-10 h-10 text-slate-200 mx-auto mb-3" />
            <p className="text-slate-900 font-bold">No credentials yet. Generate documents from Candidates.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {allCandidates.map((cred, i) => {
              const isPublic = wallet.privacySettings[cred.id] !== false;
              const verifyUrl = getVerificationURL(cred.verificationId || cred.generatedId);
              const credSettings = allSettings?.[cred.mode] || settings;
              return (
                <motion.div key={cred.id} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.05 }}
                  className="glass-card rounded-2xl p-6 group hover:shadow-xl hover:shadow-sky-500/10 transition-all duration-300">
                  <div className="flex justify-between items-start mb-4">
                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${cred.mode === 'internship' ? 'bg-sky-50' : 'bg-violet-50'}`}>
                      <FiAward className={`w-6 h-6 ${cred.mode === 'internship' ? 'text-sky-500' : 'text-violet-500'}`} />
                    </div>
                    <button onClick={() => updatePrivacySetting(cred.id, !isPublic)} className={`p-2 rounded-xl transition-all text-xs flex items-center gap-1 font-bold ${isPublic ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-400'}`}>
                      {isPublic ? <><FiEye className="w-3 h-3" /> Public</> : <><FiEyeOff className="w-3 h-3" /> Private</>}
                    </button>
                  </div>
                  <h3 className="font-semibold text-navy-900 text-base mb-1 truncate">{cred.name}</h3>
                  <div className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-1">{cred.mode === 'internship' ? 'Internship' : 'Certificate'}</div>
                  <div className="font-mono text-xs text-sky-600 bg-sky-50 px-2 py-1 rounded-lg inline-block mb-4">{cred.generatedId}</div>
                  <div className="grid grid-cols-2 gap-2">
                    <button onClick={() => downloadCertificatePDF(cred, credSettings, cred.mode).then(() => toast.success('PDF ready!'))}
                      className="flex items-center justify-center gap-1.5 py-2.5 bg-slate-900 text-white rounded-xl text-[10px] font-bold hover:bg-slate-800 transition-all">
                      <FiDownload className="w-3.5 h-3.5" /> PDF
                    </button>
                    <button onClick={() => handleShare(cred)}
                      className="flex items-center justify-center gap-1.5 py-2.5 bg-sky-600 text-white rounded-xl text-[10px] font-bold hover:bg-sky-500 transition-all">
                      <FiShare2 className="w-3.5 h-3.5" /> Share
                    </button>
                    <button onClick={() => { navigator.clipboard.writeText(verifyUrl); toast.success('Verify link copied!'); }}
                      className="flex items-center justify-center gap-1.5 py-2.5 bg-slate-100 text-slate-600 rounded-xl text-[10px] font-bold hover:bg-slate-200 transition-all">
                      <FiLink className="w-3.5 h-3.5" /> Verify Link
                    </button>
                    <button onClick={() => { navigator.clipboard.writeText(employerShareLink(cred)); toast.success('Employer link copied!'); }}
                      className="flex items-center justify-center gap-1.5 py-2.5 bg-amber-50 text-amber-700 border border-amber-100 rounded-xl text-[10px] font-bold hover:bg-amber-100 transition-all">
                      <FiUser className="w-3.5 h-3.5" /> Employer
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </section>

      {/* Badges */}
      <section>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-navy-900 flex items-center gap-3">
            <span className="w-8 h-8 rounded-xl bg-violet-50 text-violet-600 flex items-center justify-center"><FiStar className="w-4 h-4" /></span>
            Digital Badges
          </h2>
          <button onClick={() => setAddBadgeModal(true)} className="flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-xl text-xs font-bold hover:bg-violet-500 transition-all">
            <FiPlus /> Add Badge
          </button>
        </div>
        <div className="glass-card rounded-2xl p-8">
          <div className="grid grid-cols-3 md:grid-cols-6 lg:grid-cols-8 gap-6">
            {wallet.badges.map(badge => <Badge key={badge.id} type={badge.type} label={badge.label} sublabel={badge.sublabel} size={72} />)}
          </div>
        </div>
      </section>

      {/* Share Modal */}
      <Modal isOpen={!!shareModal} onClose={() => setShareModal(null)} title="Share Credential" size="sm">
        {shareModal && (
          <div className="p-6 space-y-5">
            <div className="flex justify-center">
              <QRCodeDisplay value={getVerificationURL(shareModal.verificationId || shareModal.generatedId)} size={160} />
            </div>
            <div className="space-y-2">
              {[
                { label: '🔗 LinkedIn', url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(getVerificationURL(shareModal.verificationId || shareModal.generatedId))}`, cls: 'bg-[#0077b5] text-white' },
                { label: '✖️ Twitter / X', url: `https://twitter.com/intent/tweet?url=${encodeURIComponent(getVerificationURL(shareModal.verificationId || shareModal.generatedId))}&text=Check+out+my+verified+credential!`, cls: 'bg-slate-900 text-white' },
                { label: '💬 WhatsApp', url: `https://wa.me/?text=${encodeURIComponent('My verified credential: ' + getVerificationURL(shareModal.verificationId || shareModal.generatedId))}`, cls: 'bg-[#25D366] text-white' },
              ].map(s => (
                <a key={s.label} href={s.url} target="_blank" rel="noopener noreferrer" className={`flex items-center justify-between px-5 py-3 rounded-xl font-bold text-sm ${s.cls} hover:opacity-90 transition-all`}>
                  {s.label} <FiExternalLink />
                </a>
              ))}
              <button onClick={() => { navigator.clipboard.writeText(getVerificationURL(shareModal.verificationId || shareModal.generatedId)); toast.success('Copied!'); }}
                className="w-full flex items-center justify-between px-5 py-3 bg-slate-100 text-slate-700 rounded-xl font-bold text-sm hover:bg-slate-200 transition-all">
                Copy Direct Link <FiCopy />
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Add Badge Modal */}
      <Modal isOpen={addBadgeModal} onClose={() => setAddBadgeModal(false)} title="Add New Badge" size="sm">
        <div className="p-6 space-y-4">
          <div className="flex justify-center mb-2">
            <Badge type={newBadge.type || 'skill'} label={newBadge.label || 'Preview'} sublabel={newBadge.sublabel} size={80} />
          </div>
          <div>
            <label className="block section-title mb-2">Badge Type</label>
            <select value={newBadge.type} onChange={e => setNewBadge(p => ({ ...p, type: e.target.value }))} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm bg-white focus:outline-none focus:border-sky-400">
              {['skill', 'achievement', 'completion', 'excellence'].map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
            </select>
          </div>
          <div>
            <label className="block section-title mb-2">Badge Label</label>
            <input value={newBadge.label} onChange={e => setNewBadge(p => ({ ...p, label: e.target.value }))} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-sky-400" placeholder="e.g. React Expert" />
          </div>
          <div>
            <label className="block section-title mb-2">Sub-label (optional)</label>
            <input value={newBadge.sublabel} onChange={e => setNewBadge(p => ({ ...p, sublabel: e.target.value }))} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-sky-400" placeholder="e.g. Frontend" />
          </div>
          <div className="flex justify-end gap-3">
            <button onClick={() => setAddBadgeModal(false)} className="px-6 py-2.5 bg-slate-100 text-slate-600 rounded-xl font-bold text-sm">Cancel</button>
            <button onClick={handleAddBadge} className="px-8 py-2.5 bg-violet-600 text-white rounded-xl font-bold text-sm hover:bg-violet-500 transition-all">Add Badge</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
